import { Fragment, useState } from "react";
import { Tooltip } from "@mui/material";
import moment from "moment";

const scales = {
  day: { label: "Día", unit: "day", alignment: "isoWeek", count: 14 },
  week: { label: "Semana", unit: "week", alignment: "isoWeek", count: 8 },
  month: { label: "Mes", unit: "month", alignment: "month", count: 6 },
};

const monthLabel = new Intl.DateTimeFormat("es-PE", { month: "long", year: "numeric" });
const parseDate = (date) => date ? moment(date).startOf("day") : moment.invalid();

export const TasksGantt = ({ tasks, onOpenTask, users = [] }) => {
  const [scale, setScale] = useState("week");
  const [anchor, setAnchor] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const scheduled = tasks.flatMap((task) => {
    const start = parseDate(task.fecha_inicio);
    const end = parseDate(task.fecha_fin);
    if (!end.isValid() || (start.isValid() && start.isAfter(end))) return [];
    return [{ task, start: start.isValid() ? start : end, end, deadlineOnly: !start.isValid() }];
  });
  const omitted = tasks.length - scheduled.length;

  if (!scheduled.length) {
    return <p className="tasks-empty">No hay tareas con fechas válidas para mostrar en el Gantt.</p>;
  }

  const { unit, alignment, count } = scales[scale];
  const today = moment().startOf("day");
  const earliest = moment.min(scheduled.map(({ start }) => start));
  const latest = moment.max(scheduled.map(({ end }) => end));
  const defaultAnchor = today.isBetween(earliest, latest, "day", "[]") ? today : earliest;
  const first = (anchor || defaultAnchor).clone().startOf(alignment);
  const limit = first.clone().add(count, unit);
  const position = (date) => date.diff(first, "days") / limit.diff(first, "days") * 100;
  const ticks = Array.from({ length: count }, (_, index) => {
    const start = first.clone().add(index, unit);
    const end = first.clone().add(index + 1, unit);
    return { start, width: position(end) - position(start) };
  });
  const months = [];
  for (let month = first.clone(); month.isBefore(limit);) {
    const next = moment.min(month.clone().startOf("month").add(1, "month"), limit);
    months.push({ label: monthLabel.format(month.toDate()), width: position(next) - position(month) });
    month = next;
  }
  const todayPosition = position(today);
  const showToday = todayPosition >= 0 && todayPosition < 100;
  const groups = new Map();
  scheduled.forEach((entry) => {
    const { task } = entry;
    const name = task.project_name || task.proyecto || "Tarea general";
    const key = task.id_proyecto ? `project-${task.id_proyecto}` : `name-${name}`;
    if (!groups.has(key)) groups.set(key, { name, entries: [] });
    groups.get(key).entries.push(entry);
  });
  const visible = scheduled.filter(({ start, end }) => start.isBefore(limit) && end.isSameOrAfter(first)).length;

  return (
    <>
      <div className="tasks-gantt-toolbar">
        <div className="tasks-gantt-navigation" role="group" aria-label="Navegación del cronograma">
          <button type="button" className="btn tasks-filter-toggle" aria-label="Periodo anterior"
            onClick={() => setAnchor(first.clone().subtract(count, unit))}>
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </button>
          <button type="button" className="btn tasks-filter-toggle" onClick={() => setAnchor(today)}>Ir a hoy</button>
          <button type="button" className="btn tasks-filter-toggle" aria-label="Periodo siguiente"
            onClick={() => setAnchor(limit)}>
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
        </div>
        <span className="tasks-gantt-range" aria-live="polite">
          {first.format("DD/MM/YYYY")} – {limit.clone().subtract(1, "day").format("DD/MM/YYYY")}
        </span>
        <label className="tasks-gantt-zoom">
          <span>Escala</span>
          <select className="form-select form-select-sm" value={scale} onChange={(event) => setScale(event.target.value)}>
            {Object.entries(scales).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>
      <div className="tasks-gantt-scroll" role="region" aria-label="Cronograma de tareas" tabIndex={0}>
        <div className="tasks-gantt" style={{ "--gantt-columns": count }}>
          <div className="tasks-gantt-heading">Actividad</div>
          <div className="tasks-gantt-scale">
            <div className="tasks-gantt-months">
              {months.map(({ label, width }) => <span key={label} style={{ width: `${width}%` }}>{label}</span>)}
            </div>
            <div className="tasks-gantt-ticks">
              {ticks.map(({ start, width }) => (
                <time key={start.valueOf()} dateTime={start.format("YYYY-MM-DD")} style={{ width: `${width}%` }}>
                  {scale === "month" ? start.format("MM/YY") : start.format("DD/MM")}
                </time>
              ))}
            </div>
            {showToday && <span className="tasks-gantt-today-tag" style={{ left: `${todayPosition}%` }}>Hoy</span>}
          </div>
          {Array.from(groups, ([key, { name, entries }]) => (
            <Fragment key={key}>
              <div className="tasks-gantt-label tasks-gantt-group">
                <button type="button" aria-expanded={!collapsed[key]} onClick={() =>
                  setCollapsed((current) => ({ ...current, [key]: !current[key] }))
                }>
                  <i className={`bi bi-chevron-${collapsed[key] ? "right" : "down"}`} aria-hidden="true" />
                  <span>{name}</span>
                  <small>{entries.length}</small>
                </button>
              </div>
              <div className="tasks-gantt-group" aria-hidden="true" />
              {!collapsed[key] && entries.map(({ task, start, end, deadlineOnly }) => {
                const left = Math.max(0, position(start));
                const right = Math.min(100, position(end.clone().add(1, "day")));
                const user = users?.find(({ id }) => String(id) === String(task.id_responsable));
                const responsible = user && [user.nombre, user.apellidos].filter(Boolean).join(" ");
                const dates = deadlineOnly
                  ? `Vence el ${end.format("DD/MM/YYYY")}`
                  : `${start.format("DD/MM/YYYY")} al ${end.format("DD/MM/YYYY")}`;
                const details = [task.nombre, dates, responsible, task.estado].filter(Boolean).join(" · ");
                return (
                  <Fragment key={task.id}>
                    <div className="tasks-gantt-label tasks-gantt-activity">
                      <button type="button" onClick={() => onOpenTask(task.id)}>{task.nombre}</button>
                      <small>{responsible || "Sin responsable"}</small>
                    </div>
                    <div className="tasks-gantt-track">
                      <div className="tasks-gantt-grid" aria-hidden="true">
                        {ticks.map(({ start: tick, width }) => (
                          <span key={tick.valueOf()} style={{ width: `${width}%` }}
                            className={scale === "day" && tick.isoWeekday() >= 6 ? "tasks-gantt-weekend" : undefined} />
                        ))}
                      </div>
                      {showToday && <span className="tasks-gantt-today" style={{ left: `${todayPosition}%` }} aria-hidden="true" />}
                      {right > left && (
                        <Tooltip title={details} arrow>
                          <button
                            type="button"
                            className={`tasks-gantt-bar${deadlineOnly ? " tasks-gantt-milestone" : ""}${task.estado === "completado" ? " tasks-gantt-done" : ""}`}
                            style={{ left: `${deadlineOnly ? (left + right) / 2 : left}%`, width: `${right - left}%` }}
                            aria-label={details}
                            onClick={() => onOpenTask(task.id)}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
      <p className="tasks-gantt-note">
        {visible} de {scheduled.length} actividades de esta página dentro del periodo.
        {omitted > 0 && ` Sin fechas válidas: ${omitted}.`}
      </p>
    </>
  );
};
